import { Link } from "react-router-dom";
import { Building2, ShieldCheck, LineChart, Users, Cpu, Clock, ArrowUpRight, Check } from "lucide-react";
import GradientBg from "@/components/common/GradientBg";
import HeadMeta from "@/components/common/HeadMeta";
import Reveal from "@/components/marketing/Reveal";
import { BRAND } from "@/branding.config";

const FEATURES = [
  { icon: Cpu,        title: "Dedicated hypervisors",   body: "Reserved bare-metal nodes — no neighbours, no noisy pinning surprises." },
  { icon: ShieldCheck,title: "Contractual SLA",         body: "99.99–99.999% with service credits. Custom mitigation clauses for regulated firms." },
  { icon: LineChart,  title: "Custom monitoring",       body: "Roll-up dashboards, private probes, custom SLOs. Data exportable to your SIEM." },
  { icon: Users,      title: "White-glove onboarding",  body: "A dedicated solutions engineer, migration plan, and 30-day post-launch hyper-care." },
  { icon: Clock,      title: "Net-30 billing",          body: "Wire transfer, invoicing per PO number, procurement-friendly paperwork." },
  { icon: Building2,  title: "Data residency guarantees","body":"Contractual pinning to specific EU or APAC jurisdictions with attestation." },
];

const STEPS = [
  { n: "01", title: "Kickoff call",       body: "45-minute scoping session with a solutions engineer. NDA on request." },
  { n: "02", title: "Reference architecture", body: "We ship a written architecture doc, capacity plan, and BOM within 5 business days." },
  { n: "03", title: "Pilot deployment",    body: "Live pilot on dedicated hardware, side-by-side with your current provider." },
  { n: "04", title: "Migration + hyper-care", body: "Fixed-fee migration and 30 days of dedicated Slack channel." },
];

export default function Enterprise() {
  return (
    <>
      <HeadMeta title="Enterprise" description={`Dedicated hardware, contractual SLA and white-glove onboarding from ${BRAND.app_name}.`} />
      <div className="relative">
        <GradientBg variant="hero" />

        <section className="relative mx-auto max-w-6xl px-6 pt-28 pb-24">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Enterprise</div>
            <h1 className="mt-4 text-5xl md:text-7xl font-black tracking-[-0.03em] leading-[0.95] max-w-4xl">
              Infrastructure that <span className="brand-gradient-text">signs your MSA</span>.
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">
              Dedicated capacity, custom SLAs, procurement-friendly paperwork and a real
              account team. Built for prop shops, regulated firms and platforms that can't
              afford to phone a chatbot at 3 a.m.
            </p>
            <div className="mt-10 flex gap-3">
              <Link to="/contact" className="btn-primary px-6 py-3">Talk to solutions <ArrowUpRight size={16}/></Link>
              <Link to="/sla" className="btn-outline px-6 py-3">Read the SLA</Link>
            </div>
          </Reveal>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map(({ icon: Icon, title, body }, i) => (
              <Reveal key={title} delay={i * 0.05}>
                <div className="surface p-7 h-full transition-transform hover:-translate-y-1">
                  <Icon className="text-primary" size={20}/>
                  <h3 className="mt-5 text-lg font-bold">{title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-6 py-24">
          <Reveal>
            <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Onboarding</div>
            <h2 className="mt-3 text-4xl md:text-5xl font-black tracking-tight">From RFP to production in weeks, not quarters.</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="glass p-8 h-full">
                  <div className="font-mono text-3xl brand-gradient-text font-black">{s.n}</div>
                  <div className="mt-4 text-xl font-bold">{s.title}</div>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-6 pb-32">
          <Reveal>
            <div className="glass-strong p-10 md:p-14 relative overflow-hidden">
              <div className="orb bg-violet w-[380px] h-[380px] -left-24 -top-24" />
              <div className="orb bg-pink   w-[280px] h-[280px] -right-16 -bottom-16" />
              <div className="relative">
                <h3 className="text-3xl md:text-4xl font-black tracking-tight max-w-2xl">Ready to run a pilot?</h3>
                <p className="mt-3 text-muted-foreground max-w-xl">Standard pilots run for 14 days on dedicated hardware, side-by-side with your current provider. No commitment.</p>
                <ul className="mt-6 space-y-2 text-sm">
                  <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500"/> Signed MSA + DPA</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500"/> Reference architecture doc</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500"/> Named account team</li>
                </ul>
                <div className="mt-8">
                  <Link to="/contact" className="btn-primary px-6 py-3">Start a pilot</Link>
                </div>
              </div>
            </div>
          </Reveal>
        </section>
      </div>
    </>
  );
}
